import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Check, X, AlertCircle } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * ResetPasswordLinkScreen — the page the link in the reset e-mail opens.
 *
 * ★★★ THE ROUTE IS A CONTRACT WITH A REPOSITORY THIS ONE CANNOT SEE. orella-api
 *   builds the link as PATIENT_APP_BASE + PATIENT_PASSWORD_RESET_APP_PATH +
 *   '?token=<uuid>'. If the path this screen is mounted at and that constant
 *   disagree BY ONE BYTE, the link 404s, the token is never spent, and BOTH
 *   HALVES LOOK CORRECT IN ISOLATION — nothing on either side goes red. That is
 *   why scripts/check-patient-email-reset.mjs reads the constant out of the API
 *   repo's own git blob and compares it, rather than trusting this comment.
 *   The path, today: /reset-password.
 *
 * ★★★ OPENING THIS PAGE MUST NOT SPEND THE TOKEN. Hospital and corporate mail
 *   filters (Defender for O365, Proofpoint, Mimecast) PREFETCH every link in a
 *   delivered message and win the race against the human. If a mount-time effect
 *   redeemed the token, the scanner would burn it and the patient would be handed
 *   "this link is invalid" for a mail that had just arrived. So:
 *     THE TOKEN IS SPENT ONLY BY THE POST THAT CARRIES THE NEW PASSWORD.
 *   There is no request of any kind in an effect in this file, and there must
 *   never be one. A scanner can fetch this page; it cannot submit this form.
 *
 * ★★ AND THE SCREEN DOES NOT TRY TO SAY *WHY* A LINK IS DEAD. The server answers
 *   unknown, malformed, expired, already-spent, belonging-to-a-deleted-account
 *   and lost-the-race with one sentence and one 400, because splitting them would
 *   tell somebody who has not proved they hold the mailbox that their link "was
 *   real, merely late". What this screen owes that person instead is a WAY
 *   FORWARD — "request a new link" — because "that didn't work" with no next step
 *   is how people give up and stop being patients.
 *
 * ★ THE TOKEN IS A CREDENTIAL AND IS NEVER LOGGED, never put in a console line,
 *   never written into an error message, and never sent anywhere but to the
 *   redeem call. There is no console call in this file.
 */
export default function ResetPasswordLinkScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { redeemPasswordResetLink, loading } = useUserStore()
  const { t } = useI18n()

  // ★ READ, NOT REDEEMED. Taking the token out of the query string is a local
  //   read of the URL; it contacts nothing.
  const token = (searchParams.get('token') || '').trim()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  // ★★ 'form' | 'ok' | 'dead'. A link with no token at all is 'dead' from the
  //   start — same screen, same words, same way forward as a refused one.
  const [phase, setPhase] = useState(token ? 'form' : 'dead')
  const [redirecting, setRedirecting] = useState(false)

  // ★★★ THIS EFFECT NAVIGATES. IT MAKES NO REQUEST, AND NOTHING IN THIS FILE
  //   MAY EVER ADD ONE — see the block above on mail-scanner prefetch.
  useEffect(() => {
    if (phase !== 'ok') return undefined
    setRedirecting(true)
    const handle = setTimeout(() => navigate('/signin', { replace: true }), 2000)
    return () => clearTimeout(handle)
  }, [phase, navigate])

  const requirements = [
    { key: 'length', test: (p) => p.length >= 8, label: t('auth.passwordMinLength') },
    { key: 'match', test: (p) => p && confirmPassword && p === confirmPassword, label: t('auth.passwordsMatch') },
  ]
  const allRequirementsMet = requirements.every((req) => req.test(password))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // ★★★ THE LENGTH CHECK IS LOAD-BEARING AND NOT COSMETIC. The server answers a
    //   short password with a 400 too — a fact about the password, not about the
    //   token — and the store maps 400 to 'link-dead'. Enforcing the SAME minimum
    //   here is what makes that mapping correct: a person who types six
    //   characters must not be told their link is dead and sent back to the
    //   start. Do not remove this without changing the store with it.
    if (password.length < 8) {
      setError(t('auth.passwordTooShort'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'))
      return
    }

    const result = await redeemPasswordResetLink(token, password)

    if (result.outcome === 'ok') {
      setPhase('ok')
    } else if (result.outcome === 'link-dead') {
      setPhase('dead')
    } else {
      // ★★ 'retry' IS NOT 'dead'. The server could not answer — a 503, a timeout,
      //   a lost connection. Telling the patient their link is invalid over a
      //   minute-long outage throws away a token that is still perfectly good.
      //   The form stays exactly where it is so they can press the button again.
      setError(t('errors2.somethingWentWrong'))
    }
  }

  if (phase === 'ok') {
    return (
      <ResponsiveWrapper className="bg-white">
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 py-8 flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
            {t('auth.passwordResetSuccess')}
          </h1>
          {/* ★ NO SESSION IS MINTED BY THE RESET — the server deliberately hands
              back no token, so the next step really is the sign-in screen. */}
          <p className="text-gray-500 text-center mb-2">
            {t('auth.redirectingToSignIn')}
          </p>
          {/* PROD-1345: a reset signs the account out everywhere; say so. */}
          <p className="text-gray-500 text-center mb-8" role="status">
            {t('auth.signedOutAllDevices')}
          </p>
          {redirecting && (
            <OrellaSpinner size={24} />
          )}
        </div>
      </ResponsiveWrapper>
    )
  }

  if (phase === 'dead') {
    return (
      <ResponsiveWrapper className="bg-white" bottomInset={false}>
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center">
          <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain mb-8" />
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          {/* ★★★ ONE SENTENCE FOR SIX DIFFERENT SERVER-SIDE CAUSES, AND FOR A LINK
              THAT CARRIED NO TOKEN AT ALL. Naming the cause would be telling
              somebody who has not proved they hold the mailbox which of those it
              was. The button below is what this screen owes them instead. */}
          <p className="text-gray-600 text-center md:text-lg mb-8">
            {t('auth.resetLinkInvalid')}
          </p>
          </div>
          {/* PROD-1194: the two actions are pinned at the bottom of the screen. */}
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button
              onClick={() => navigate('/forgot-password/email', { replace: true })}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full transition-opacity breathe-blue"
            >
              {t('auth.requestNewLink')}
            </button>
            <button
              onClick={() => navigate('/signin', { replace: true })}
              className="w-full py-4 mt-3 bg-gray-200 text-gray-600 font-semibold rounded-full"
            >
              {t('auth.signIn')}
            </button>
          </BottomActionBar>
        </div>
      </ResponsiveWrapper>
    )
  }

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
        <div className="flex items-center gap-4 mb-6">
          <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain" />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-orella-500 mb-2">
          {t('auth.newPasswordTitle')}
        </h1>
        <p className="text-gray-500 mb-8 md:text-lg">
          {t('auth.newPasswordDesc')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* PROD-1194: the form fills the column; Save (and Cancel) are pinned at the bottom. */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="space-y-5">
          <div>
            <label className="block text-sm text-gray-500 mb-1" htmlFor="link-new-password">
              {t('auth.newPassword')}
            </label>
            <div className="relative">
              <input
                id="link-new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                aria-label={t('auth.newPassword')}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-500 mb-1" htmlFor="link-confirm-password">
              {t('auth.confirmPassword')}
            </label>
            <div className="relative">
              <input
                id="link-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                aria-label={t('auth.confirmPassword')}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="space-y-2 pt-2">
            {requirements.map((req) => (
              <div
                key={req.key}
                className={`flex items-center gap-2 text-sm ${req.test(password) ? 'text-green-600' : 'text-gray-400'}`}
              >
                {req.test(password) ? (
                  <Check size={16} className="flex-shrink-0" />
                ) : (
                  <X size={16} className="flex-shrink-0" />
                )}
                <span>{req.label}</span>
              </div>
            ))}
          </div>

          </div>

          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button
              type="submit"
              disabled={loading || !allRequirementsMet}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 transition-opacity breathe-blue"
            >
              {loading ? t('common.saving') : t('auth.savePassword')}
            </button>

            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="w-full py-4 mt-3 bg-gray-200 text-gray-600 font-semibold rounded-full"
            >
              {t('common.cancel')}
            </button>
          </BottomActionBar>
        </form>
      </div>
    </ResponsiveWrapper>
  )
}
